import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useLoginMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks/redux';
import { setToken } from '@/store/slices/authSlice';
import toast from 'react-hot-toast';
import { LoginFormData, loginSchema } from '@/utils/validations';
import CustomInput from '@/components/common/CustomInput';
import { Button } from '@/components/ui/button';
import { Icons } from '@/utils/icons';

function LoginPage() {
  const [login, { isLoading, error }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { control, handleSubmit, reset } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const payload = {
        username: data.email,
        password: data.password,
      };
      const res = await login(payload).unwrap();
      if (res?.accessToken) {
        dispatch(setToken(res.accessToken));
        reset();
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      console.error('Login failed:', err);
      toast.error(err?.data?.message || 'Login failed!');
    } finally {
    }
  };
  return (
    <div className="w-full rounded-3xl bg-login-gradient shadow-navy-shadow ring-1 ring-rsplay-gold/30 overflow-hidden">
      {/* Gold accent bar */}
      <div className="h-1 w-full bg-golden-gredient" />

      <div className="space-y-7 px-8 py-12 sm:px-10">
        <div className="flex flex-col items-center gap-3">
          <img
            src="/betexLogo.png"
            alt="Betex"
            className="max-w-[200px] drop-shadow-[0_0_14px_rgba(234,173,7,0.35)]"
          />
          <div className="text-center">
            <h1 className="font-heading text-2xl font-semibold text-white tracking-wide">
              Welcome Back
            </h1>
            <p className="mt-1 text-sm text-rsplay-gold-light/70">Sign in to manage bet results</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Username */}
          <CustomInput
            name="email"
            type="email"
            control={control}
            label="Email"
            placeholder="abc@gmail.com"
            autoComplete="email"
            icon={Icons.userTwo}
          />

          {/* Password */}
          <CustomInput
            name="password"
            control={control}
            label="Password"
            type="password"
            placeholder="Enter your password"
            autoComplete="current-password"
            icon={Icons.lock}
            inputRules={{
              noSpace: true,
              maxLength: 20,
            }}
          />
          <div className="pt-2 w-full flex justify-center">
            <Button
              type="submit"
              className="w-full h-12 px-2 bg-btn-gradient hover:bg-primary-brown1 rounded-xl text-base font-semibold uppercase tracking-wide text-primary-foreground shadow-gold-shadow transition-transform hover:scale-[1.01] flex justify-center items-center"
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Login'}
            </Button>
          </div>
        </form>

        <p className="text-center text-xs text-white/40">
          Authorized personnel only &middot; Results Panel
        </p>
      </div>
    </div>
  );
}
export default LoginPage;
